import { defineStore } from 'pinia';
import { reactive, toRefs } from 'vue';
import {
  setLocalStorageItem,
  getLocalStorageItem,
  STORAGE_KEYS,
  removeLocalStorageItem,
} from '@/utils/localStorage';
import { type AxleAuthRequest, type Token } from '@/types/auth';
import { useGmApi } from '@/composables/useGmApi';
import { useUserStore } from './userStore';
import { useMainStore } from './mainStore';

export const getCachedAuthToken = () => getLocalStorageItem<string>(STORAGE_KEYS.TOKEN);
export const isAuthenticated = () => !!getCachedAuthToken();

interface MainStoreState {
  token: string | null;
}

const DEFAULT_STATE = {
  token: getCachedAuthToken(),
};

function cloneDefaultState() {
  return structuredClone(DEFAULT_STATE);
}

export const useAuthStore = defineStore('auth', () => {
  const state = reactive<MainStoreState>(cloneDefaultState());

  async function getTokenAxle(request: AxleAuthRequest) {
    const data = await useGmApi().loginAxle(request);
    setToken(data.token);
    const userStore = useUserStore();
    userStore.setUser(data.user);
    userStore.setPermissions(data.permissions);
    if (data.impersonator) {
      userStore.setImpersonator(data.impersonator);
    }
    useMainStore().setTeamTwrn((data.permissions && data.permissions[0]?.twrn) ? data.permissions[0].twrn : null);
    useMainStore().setOrgTwrn(data.org.twrn);
  }

  function setToken(token: Token) {
    state.token = token.token;
    setLocalStorageItem<string>(STORAGE_KEYS.TOKEN, token.token, new Date(token.expires_at));
  }

  function unsetToken() {
    state.token = null;
    removeLocalStorageItem(STORAGE_KEYS.TOKEN);
  }

  const $reset = () => {
    Object.assign(state, cloneDefaultState());
  };

  return {
    ...toRefs(state),
    getTokenAxle,
    setToken,
    unsetToken,
    $reset,
  };
});
